// frontend/app/components/lessonPlans/authoring/BlockPromptFields.js
//
// The field kinds the Loop block types brought with them: the reflection
// prompt list, and the on/off switches behind `ai_feedback` and `confidence`.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { IconButton, TextField } from "./WizardFields";

const slugifyPromptKey = (label) =>
  String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);

const uniquePromptKey = (base, taken) => {
  const seed = base || "prompt";
  if (!taken.has(seed)) return seed;
  let n = 2;
  while (taken.has(`${seed}_${n}`)) n += 1;
  return `${seed}_${n}`;
};

/**
 * The sentence starters on a reflection block. Each answer is filed under its
 * `key`, so the key follows the label only while it is still the one we
 * generated — once an author edits it, rewriting it would strand every answer
 * already saved against the old one. Same rule as concept keys.
 */
export const PromptListField = ({ colors, block, field, onPatch, t }) => {
  const translate = t || ((key) => key);
  const prompts = block[field.key] || [];
  const atLimit = Boolean(field.maxItems) && prompts.length >= field.maxItems;

  const commit = (next) => onPatch({ [field.key]: next });

  const update = (index, patch) =>
    commit(prompts.map((p, i) => (i === index ? { ...p, ...patch } : p)));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= prompts.length) return;
    const next = [...prompts];
    next[index] = prompts[target];
    next[target] = prompts[index];
    commit(next);
  };

  return (
    <View>
      {prompts.map((prompt, index) => (
        <View key={`prompt-${index}`} style={styles.promptRow}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <TextField
              colors={colors}
              value={prompt.label}
              onChange={(label) =>
                update(index, {
                  label,
                  key:
                    prompt.key === slugifyPromptKey(prompt.label)
                      ? uniquePromptKey(
                          slugifyPromptKey(label),
                          new Set(
                            prompts.filter((_, i) => i !== index).map((p) => p.key)
                          )
                        )
                      : prompt.key,
                })
              }
              placeholder={`${translate("Sentence starter")} ${index + 1}`}
            />
            <View style={styles.promptKeyRow}>
              <Text style={[styles.promptKeyLabel, { color: colors.textMuted }]}>
                {translate("Saved as")}
              </Text>
              <View style={{ flex: 1, minWidth: 0 }}>
                <TextField
                  colors={colors}
                  value={prompt.key}
                  onChange={(key) => update(index, { key })}
                  placeholder="before"
                  accessibilityLabel={`${translate("Answer key")} ${index + 1}`}
                />
              </View>
            </View>
          </View>

          <View style={styles.promptActions}>
            <IconButton
              colors={colors}
              icon="chevron-up"
              label={translate("Move prompt up")}
              disabled={index === 0}
              onPress={() => move(index, -1)}
            />
            <IconButton
              colors={colors}
              icon="chevron-down"
              label={translate("Move prompt down")}
              disabled={index === prompts.length - 1}
              onPress={() => move(index, 1)}
            />
            <IconButton
              colors={colors}
              icon="close"
              label={`${translate("Remove prompt")} ${index + 1}`}
              danger
              disabled={prompts.length <= 1}
              onPress={() => commit(prompts.filter((_, i) => i !== index))}
            />
          </View>
        </View>
      ))}

      <TouchableOpacity
        onPress={() =>
          commit([
            ...prompts,
            {
              key: uniquePromptKey(
                `prompt_${prompts.length + 1}`,
                new Set(prompts.map((p) => p.key))
              ),
              label: "",
            },
          ])
        }
        disabled={atLimit}
        accessibilityRole="button"
        accessibilityState={{ disabled: atLimit }}
        accessibilityLabel={translate(field.addLabel || "Add prompt")}
        style={[
          styles.addRow,
          { borderColor: colors.borderStrong, opacity: atLimit ? 0.35 : 1 },
        ]}
      >
        <MaterialCommunityIcons name="plus" size={14} color={colors.accentPrimary} />
        <Text style={[styles.addText, { color: colors.accentPrimary }]}>
          {translate(field.addLabel || "Add prompt")}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

/** A single on/off switch, for the `ai_feedback`-shaped fields. */
export const ToggleField = ({ colors, label, value, onChange, t }) => {
  const translate = t || ((key) => key);
  const on = Boolean(value);
  return (
    <TouchableOpacity
      onPress={() => onChange(!on)}
      accessibilityRole="switch"
      accessibilityState={{ checked: on }}
      accessibilityLabel={label}
      style={[
        styles.toggle,
        {
          backgroundColor: on ? colors.accentGlow : colors.subtleBg,
          borderColor: on ? colors.accentPrimary : colors.border,
        },
      ]}
    >
      <MaterialCommunityIcons
        name={on ? "check-circle" : "circle-outline"}
        size={16}
        color={on ? colors.accentPrimary : colors.textMuted}
      />
      <Text
        style={[
          styles.toggleText,
          { color: on ? colors.accentPrimary : colors.textSecondary },
        ]}
      >
        {on ? translate("On") : translate("Off")}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  promptRow: { flexDirection: "row", alignItems: "flex-start", gap: 7, marginBottom: 10 },
  promptKeyRow: { flexDirection: "row", alignItems: "center", gap: 7, marginTop: 5 },
  promptKeyLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  promptActions: { flexDirection: "row", gap: 4, marginTop: 7 },

  addRow: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 9,
    borderWidth: 1,
    borderStyle: "dashed",
    paddingHorizontal: 10,
    paddingVertical: 7,
    gap: 5,
  },
  addText: { fontSize: 12, fontWeight: "700" },

  toggle: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 7,
    gap: 6,
  },
  toggleText: { fontSize: 12, fontWeight: "700" },
});
